import React from 'react';
import { Check, Home, Building, Crown, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const PackagesSection = () => {
  const { t } = useLanguage();

  const packages = [
    {
      id: 1,
      name: t('packages.essential.name'),
      target: t('packages.essential.target'),
      icon: Home,
      features: [
        t('packages.features.billPayments'),
        t('packages.features.monthlyDeep'),
        t('packages.features.emergency'),
        t('packages.features.inspection'),
        t('packages.features.keyHolding'),
        t('packages.features.airportTransfer')
      ],
      highlight: t('packages.essential.highlight')
    },
    {
      id: 2,
      name: t('packages.rental.name'),
      target: t('packages.rental.target'),
      icon: Building,
      features: [
        t('packages.features.everythingEssential'),
        t('packages.features.listingManagement'),
        t('packages.features.guestComm'),
        t('packages.features.checkInOut'),
        t('packages.features.professionalCleaning'),
        t('packages.features.welcomeKit'),
        t('packages.features.support247')
      ],
      highlight: t('packages.rental.highlight')
    },
    {
      id: 3,
      name: t('packages.premium.name'),
      target: t('packages.premium.target'),
      icon: Crown,
      features: [
        t('packages.features.everythingRental'),
        t('packages.features.airportCoord'),
        t('packages.features.groceries'),
        t('packages.features.excursions'),
        t('packages.features.handyman'),
        t('packages.features.vipConcierge'),
        t('packages.features.restaurantBookings')
      ],
      highlight: t('packages.premium.highlight')
    }
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="packages" className="py-24 tropical-gradient relative" aria-label="Property Management Pricing Packages">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#000080] bg-[#FFFF00] font-semibold tracking-wider uppercase text-sm mb-4 px-4 py-1 rounded-full">
            {t('packages.badge')}
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-[#000080] mb-6">
            {t('packages.title1')}{' '}
            <span className="text-tropical-gradient">{t('packages.title2')}</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-4">
            {t('packages.subtitle')}
          </p>
          <div className="inline-flex items-center gap-2 bg-[#FFFF00] text-[#000080] rounded-full px-6 py-3 shadow-lg">
            <span className="font-bold">{t('packages.priceStart')}</span>
          </div>
        </div>

        {/* Packages grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {packages.map((pkg, index) => {
            const IconComponent = pkg.icon;
            const isPopular = index === 1;
            const iconBgColors = ['from-[#22C55E]/20 to-[#22C55E]/10', 'from-[#8B5CF6]/20 to-[#8B5CF6]/10', 'from-[#FF6B35]/20 to-[#FF6B35]/10'];
            const highlightColors = ['bg-[#22C55E]/10', 'bg-[#8B5CF6]/10', 'bg-[#FF6B35]/10'];
            
            return (
              <div
                key={pkg.id}
                className={`package-card card-tropical bg-white rounded-2xl overflow-hidden shadow-xl ${
                  isPopular ? 'ring-2 ring-[#8B5CF6] md:-translate-y-4' : ''
                }`}
              >
                {/* Popular badge */}
                {isPopular && (
                  <div className="bg-[#8B5CF6] text-white text-center py-2 font-bold text-sm">
                    {t('packages.mostPopular')}
                  </div>
                )}

                <div className="p-8">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${iconBgColors[index]} flex items-center justify-center mb-6`}>
                    <IconComponent className="w-8 h-8 text-[#000080]" />
                  </div>

                  {/* Package name */}
                  <h3 className="font-display text-2xl font-bold text-[#000080] mb-2">
                    {pkg.name}
                  </h3>
                  <p className="text-gray-500 text-sm mb-6">{pkg.target}</p>

                  {/* Highlight */}
                  <div className={`${highlightColors[index]} rounded-lg px-4 py-2 mb-6`}>
                    <p className="text-[#000080] font-medium text-sm">{pkg.highlight}</p>
                  </div>

                  {/* Features list */}
                  <ul className="space-y-4 mb-8">
                    {pkg.features.map((feature, idx) => (
                      <li key={`${pkg.id}-feature-${idx}`} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-[#22C55E]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-[#22C55E]" />
                        </div>
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA button */}
                  <Button
                    onClick={scrollToContact}
                    className={`w-full btn-tropical ${
                      isPopular
                        ? 'bg-[#8B5CF6] hover:bg-[#7C3AED] text-white'
                        : 'bg-[#000080] hover:bg-[#000060] text-white'
                    } font-semibold py-6`}
                  >
                    {t('nav.getStarted')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PackagesSection;
